import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@demand-iq/core';
import useHasPermission from 'hooks/useHasPermission';
import { BarChart3, Building, Building2, CreditCard, Network, User } from 'lucide-react';
import { useAuth } from 'pages/AuthProvider';
import { useUserInfo } from 'pages/UserProvider';
import {
  companyDetails,
  companyListing,
  loginRoute,
  organizationListing,
  plansLanding,
  productAnalyticsDashboard,
  profileDetails,
} from 'routes/routes';

const DIQ_ADMIN = 'DIQAdmin';
const ADMIN = 'Admin';
const DIQ_SUPPORT = 'DIQSupport';
const EXCLUDE_TEAM_MANAGER = [DIQ_ADMIN, DIQ_SUPPORT, ADMIN];

export type AccountMenuItem = Readonly<{
  key: string;
  label: string;
  icon: React.ElementType;
  onClick: () => void;
}>;

export type AccountMenu = Readonly<{
  /** Role-gated navigation destinations (excludes sign out). */
  items: AccountMenuItem[];
  signOut: () => void;
  displayName: string;
  companyName: string;
  initials: string;
}>;

/**
 * The account menu's contents and role-gating, shared by the desktop sidebar
 * dropdown (`AccountMenuItems`) and the mobile bottom-nav account sheet, so the
 * permission logic lives in one place and only the presentation differs.
 */
const useAccountMenu = (): AccountMenu => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, company } = useUserInfo();
  const { logout } = useAuth();
  const hasPermission = useHasPermission();
  const isDIQAdmin = hasPermission({ roles: [DIQ_ADMIN] });
  const isExcludeTeamManager = hasPermission({ roles: EXCLUDE_TEAM_MANAGER });
  const isAdminOrDIQAdmin = hasPermission({ roles: [DIQ_ADMIN, ADMIN] });
  const isOwnCompany = company?.id === user?.company?.id;
  const hasOrganization = !!user?.company?.organizationId;
  const showCompanies = (isOwnCompany || hasOrganization) && isAdminOrDIQAdmin;
  const isOwnAdmin = isOwnCompany && isDIQAdmin;

  const items: AccountMenuItem[] = [
    { key: 'profile', label: t('nav-profile', 'Your Profile'), icon: User, onClick: () => navigate(profileDetails) },
    ...(isExcludeTeamManager
      ? [
          {
            key: 'company-settings',
            label: t('nav-company-settings', 'Company Settings'),
            icon: Building2,
            onClick: () => navigate(companyDetails),
          },
        ]
      : []),
    ...(showCompanies
      ? [
          {
            key: 'companies',
            label: t('nav-companies', 'Company Accounts'),
            icon: Building,
            onClick: () => navigate(companyListing),
          },
        ]
      : []),
    ...(isOwnAdmin
      ? [{ key: 'plans', label: t('nav-plans', 'Plans'), icon: CreditCard, onClick: () => navigate(plansLanding) }]
      : []),
    ...(isOwnAdmin
      ? [
          {
            key: 'organizations',
            label: t('nav-organizations', 'Parent Organizations'),
            icon: Network,
            onClick: () => navigate(organizationListing),
          },
        ]
      : []),
    ...(isOwnAdmin
      ? [
          {
            key: 'performance',
            label: t('nav-product-analytics', 'Performance Dashboard'),
            icon: BarChart3,
            onClick: () => navigate(productAnalyticsDashboard),
          },
        ]
      : []),
  ];

  const signOut = () => {
    logout();
    navigate(loginRoute);
  };

  const displayName = user?.userProfile?.name ?? user?.username ?? '';
  const companyName = company?.companyName ?? '';
  const initials = getInitials(displayName, user?.username);

  return { items, signOut, displayName, companyName, initials };
};

export default useAccountMenu;
